import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth, adminApi } from "@/lib/auth";
import { fileUrl } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import {
  SignOut, ArrowClockwise, X, FileArrowDown, Trash, MagnifyingGlass,
  House, Handshake, EnvelopeSimple, Tag, CaretRight,
} from "@phosphor-icons/react";

const TABS = [
  { key: "property", label: "Property", icon: House },
  { key: "broker", label: "Broker Deals", icon: Handshake },
  { key: "contact", label: "Contact", icon: EnvelopeSimple },
];

const STATUSES = ["new", "reviewing", "contacted", "archived"];
const STATUS_STYLE = {
  new: "bg-gold/20 text-forest border-gold",
  reviewing: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-green-50 text-green-700 border-green-200",
  archived: "bg-gray-100 text-gray-500 border-gray-200",
};

const HIDDEN = new Set(["id", "type", "status", "tags", "admin_notes", "created_at", "updated_at", "files", "_id"]);
const humanize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const primaryName = (row) => row.owner_name || row.broker_name || row.name || "—";
const primaryContact = (row) => row.owner_email || row.email || "—";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("property");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const reqSeq = useRef(0);

  const loadStats = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/stats"); setStats(data); } catch {}
  }, []);

  const load = useCallback(async (kind) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/submissions/${kind}`);
      if (seq !== reqSeq.current) return; // ignore stale/out-of-order response
      setRows(data);
    } catch {
      if (seq === reqSeq.current) toast.error("Could not load submissions.");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const patch = async (row, body) => {
    try {
      const { data } = await adminApi.patch(`/admin/submissions/${tab}/${row.id}`, body);
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      if (selected?.id === row.id) setSelected(data);
      loadStats();
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await adminApi.delete(`/admin/submissions/${tab}/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setSelected(null);
      loadStats();
      toast.success("Submission deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && (r.status || "new") !== statusFilter) return false;
    if (!query) return true;
    const blob = JSON.stringify(r).toLowerCase();
    return blob.includes(query.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <Seo title="Submissions Dashboard" description="Redwood Capital Holdings admin dashboard." path="/admin" />

      {/* Top bar */}
      <header className="bg-forest text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 44 44" className="h-8 w-8" aria-hidden>
              <rect x="1" y="1" width="42" height="42" rx="2" fill="none" stroke="#C9A875" strokeWidth="1.5" />
              <path d="M22 8 L22 36 M22 16 C 15 16 13 22 22 22 C 31 22 29 16 22 16 M22 24 C 16 24 14 30 22 30 C 30 30 28 24 22 24" stroke="#C9A875" strokeWidth="1.6" fill="none" strokeLinecap="round" />
            </svg>
            <div className="leading-none">
              <div className="font-serif text-base font-semibold">Redwood Capital</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Submissions</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/60">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="admin-logout">
              <SignOut size={18} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs + stats */}
        <div className="flex flex-wrap gap-3 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const s = stats[t.key];
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setStatusFilter("all"); setQuery(""); setRows([]); setSelected(null); }}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2.5 px-5 py-3 rounded-sm border text-sm font-semibold transition-colors ${
                  active ? "bg-forest text-white border-forest" : "bg-white text-forest border-line hover:border-gold"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-gold" : ""} />
                {t.label}
                {s && (
                  <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/15 text-white" : "bg-forest/5 text-ink"}`}>
                    {s.total}{s.new > 0 ? ` · ${s.new} new` : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search submissions…"
              data-testid="admin-search"
              className="w-full rounded-sm border border-line bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3.5 py-2 rounded-sm text-xs font-semibold uppercase tracking-wide border transition-colors ${
                  statusFilter === s ? "bg-forest text-white border-forest" : "bg-white text-ink border-line hover:border-gold"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button onClick={() => { load(tab); loadStats(); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-line bg-white text-sm font-medium text-forest hover:border-gold" data-testid="admin-refresh">
            <ArrowClockwise size={16} /> Refresh
          </button>
        </div>

        {/* List */}
        <div className="bg-white border border-line rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-ink">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-ink" data-testid="admin-empty">No submissions found.</div>
          ) : (
            <ul className="divide-y divide-line">
              {filtered.map((row) => (
                <li key={row.id}>
                  <button
                    onClick={() => setSelected(row)}
                    className="w-full text-left px-5 py-4 hover:bg-cream transition-colors flex items-center gap-4"
                    data-testid={`row-${row.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5">
                        <span className="font-semibold text-forest truncate">{primaryName(row)}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLE[row.status || "new"]}`}>
                          {row.status || "new"}
                        </span>
                      </div>
                      <div className="text-sm text-ink truncate mt-0.5">
                        {primaryContact(row)}
                        {row.reason ? ` · ${row.reason}` : ""}
                        {row.county ? ` · ${row.county} County` : ""}
                        {row.acreage ? ` · ${row.acreage} ac` : ""}
                      </div>
                    </div>
                    {row.files?.length > 0 && (
                      <span className="text-xs text-ink flex items-center gap-1"><FileArrowDown size={14} /> {row.files.length}</span>
                    )}
                    <span className="text-xs text-ink/60 hidden md:block">{fmtDate(row.created_at)}</span>
                    <CaretRight size={16} className="text-ink/40" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-forest/40 z-40" onClick={() => setSelected(null)} data-testid="drawer-overlay" />
          <aside className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-2xl overflow-y-auto" data-testid="detail-drawer">
            <div className="sticky top-0 bg-forest text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl font-semibold">{primaryName(selected)}</h2>
                <p className="text-white/50 text-xs mt-0.5">{fmtDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/70 hover:text-white" data-testid="drawer-close"><X size={22} /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-forest mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => patch(selected, { status: s })}
                      data-testid={`status-${s}`}
                      className={`px-3.5 py-2 rounded-sm text-xs font-bold uppercase tracking-wide border transition-colors ${
                        (selected.status || "new") === s ? STATUS_STYLE[s] : "bg-white text-ink border-line hover:border-gold"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-forest mb-2">Details</p>
                <dl className="border border-line rounded-sm divide-y divide-line">
                  {Object.entries(selected).filter(([k, v]) => !HIDDEN.has(k) && v !== "" && v !== null).map(([k, v]) => (
                    <div key={k} className="flex gap-4 px-4 py-2.5 text-sm">
                      <dt className="w-40 shrink-0 font-medium text-forest">{humanize(k)}</dt>
                      <dd className="text-ink break-words">
                        {k === "listing_url" ? <a href={v} target="_blank" rel="noreferrer" className="text-gold underline break-all">{v}</a> : String(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* Files */}
              {selected.files?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-forest mb-2">Uploaded Files</p>
                  <ul className="space-y-2">
                    {selected.files.map((f) => (
                      <li key={f.id}>
                        <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-forest border border-line rounded-sm px-4 py-2.5 hover:border-gold transition-colors" data-testid={`file-${f.id}`}>
                          <FileArrowDown size={16} className="text-gold" />
                          <span className="truncate">{f.original_filename}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-forest mb-2 flex items-center gap-1"><Tag size={14} /> Tags</p>
                <TagEditor tags={selected.tags || []} onChange={(tags) => patch(selected, { tags })} />
              </div>

              {/* Notes */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-forest mb-2">Internal Notes</p>
                <NotesEditor value={selected.admin_notes || ""} onSave={(admin_notes) => patch(selected, { admin_notes })} />
              </div>

              <button onClick={() => remove(selected)} className="inline-flex items-center gap-2 text-sm font-semibold text-destructive hover:underline" data-testid="drawer-delete">
                <Trash size={16} /> Delete submission
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

function TagEditor({ tags, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5 bg-forest/5 border border-line rounded-full px-3 py-1 text-xs text-forest">
            {t}
            <button onClick={() => onChange(tags.filter((x) => x !== t))} className="text-ink hover:text-destructive"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Add a tag…"
          data-testid="tag-input"
          className="flex-1 rounded-sm border border-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gold"
        />
        <button onClick={add} className="px-4 py-2 rounded-sm bg-forest text-white text-sm font-medium">Add</button>
      </div>
    </div>
  );
}

function NotesEditor({ value, onSave }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const dirty = text !== value;
  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder="Add internal notes about this lead…"
        data-testid="notes-input"
        className="w-full rounded-sm border border-line bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-gold resize-y"
      />
      {dirty && (
        <button onClick={() => onSave(text)} className="mt-2 px-4 py-2 rounded-sm bg-gold text-forest text-sm font-semibold" data-testid="notes-save">Save notes</button>
      )}
    </div>
  );
}
